<script lang="ts">
	import { DeviceGroup, DeviceStatusListener, ProjectorPowerManager } from "luxedo-data"
	import { Reactivity } from "luxedo-reactivity"
	import { LoadingSpinner } from "misc-ui"
	import { onMount } from "svelte"

	interface Props {
		device: DeviceGroup
		showControls?: boolean
	}

	let { device, showControls = true }: Props = $props()

	$effect.pre(() => {
		Reactivity.useProps(device, ["name", "isDeactivated"])
	})

	let deviceName = $derived(Reactivity.propOf(device, "name"))
	let deactivated = $derived(Reactivity.propOf(device, "isDeactivated"))

	let deviceStatusListener = $derived(new DeviceStatusListener(device))

	$effect(() => {
		return deviceStatusListener.listen()
	})

	let deviceStatusText = $derived(deviceStatusListener.statusText)
	let deviceStatusColor = $derived(deviceStatusListener.statusColor)

	function onWakeUp(e: MouseEvent) {
		if (!(device instanceof DeviceGroup)) return

		e.stopPropagation()
		ProjectorPowerManager.setPower(device, "ON")
	}

	function onSleep(e: MouseEvent) {
		if (!(device instanceof DeviceGroup)) return

		e.stopPropagation()
		ProjectorPowerManager.setPower(device, "OFF")
	}

	let isChanging = $state(false)

	onMount(() => {
		return ProjectorPowerManager.subscribe((ctx) => {
			isChanging = ctx[device.id]?.isChanging ?? false
		})
	})

	$inspect("PREVIEW GROUP", showControls)
</script>

<h1 class="device-name">{deviceName}</h1>
<span class="projector-status">
	{#if deactivated}
		<div class="projector-status-indicator" style="background-color: var(--color-text);"></div>
		Deactivated
	{:else}
		<div
			class="projector-status-indicator"
			class:isChanging
			style="--color-status: {deviceStatusColor};"
		></div>
		{deviceStatusText}
		{#if showControls}
			<div class="device-control">
				{#if isChanging || deviceStatusText === "Waking up..." || deviceStatusText === "Cooling down..."}
					<LoadingSpinner height="1em" color={"var(--color-main)"} />
				{:else if deviceStatusText === "Asleep" || deviceStatusText === "Partially Ready"}
					<button
						class="control-button small outline-button"
						onclick={onWakeUp}
						disabled={isChanging}>Wake</button
					>
				{:else if deviceStatusText === "Ready"}
					<button
						class="control-button small outline-button alt sleep-button"
						onclick={onSleep}
						disabled={isChanging}>Sleep</button
					>
				{/if}
			</div>
		{/if}
	{/if}
</span>

<style>
	button {
		line-height: 1em;
	}
</style>
